import { Component, OnDestroy, OnInit } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { EmployeesService } from '../../../employees/employees.service';
import { Employee } from '../../../employees/employees.model';
import { buildFilterQuery, getDefaultProfilePicUrl, handleImageError } from '../../../utils';
import { PaginatedResponse } from '../../../common/models/common.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DealersService } from '../../../dealers/dealers.service';
import { Dealer } from '../../../dealers/dealers.model';

@Component({
  selector: 'app-party-visits',
  standalone: false,
  templateUrl: './party-visits.component.html',
  styleUrl: './party-visits.component.scss',
})
export class PartyVisitsComponent implements OnInit, OnDestroy {
  protected isEmployeesDataLoading: boolean = false;
  protected employees: Employee[] = [];
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected totalCount: number = 0;
  protected searchTerm: string = '';
  protected currentPage: number = 1;
  protected designations: { id: string; title: string }[] = [];
  protected selectedDesignation: string | null = null;

  protected readonly monthOptions = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  protected dealersList: Dealer[] = [];
  protected exportFilters = {
    year: new Date().getFullYear(),
    month: null as number | null,
    date: '',
    dealer: null,
  };
  protected isExportingVisits: boolean = false;

  protected visitsSummaryMode: 'single' | 'range' = 'single';
  protected visitsSummaryFilters = {
    date: '',
    from_date: '',
    to_date: '',
  };
  protected isExportingVisitsSummary: boolean = false;

  private _employeesSubscription!: Subscription;
  private _subs = new Subscription();
  private searchDebounceTimer!: any;

  constructor(
    private _employeesService: EmployeesService,
    private _dealersService: DealersService
  ) {}

  ngOnInit(): void {
    this._getEmployeesList();
    this._loadDealers();
    this._loadDesignations();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this._getEmployeesList();
  }

  protected onSearchKeyUp(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this._getEmployeesList();
    }, 500);
  }

  protected onDesignationChange(): void {
    this.currentPage = 1;
    this._getEmployeesList();
  }

  protected onExportDealerVisits(): void {
    this.isExportingVisits = true;

    const { year, month, date, dealer } = this.exportFilters;
    const filename = date
      ? `dealer_visits_${date}.xlsx`
      : month
        ? `dealer_visits_${year}_${String(month).padStart(2, '0')}.xlsx`
        : `dealer_visits_${year}.xlsx`;

    this._subs.add(
      this._dealersService
        .exportVisits({ year, month, date: date || undefined, dealer: dealer || undefined })
        .pipe(finalize(() => (this.isExportingVisits = false)))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          },
        })
    );
  }

  protected onExportVisitsSummary(): void {
    const { date, from_date, to_date } = this.visitsSummaryFilters;
    const filters = this.visitsSummaryMode === 'single' ? { date } : { from_date, to_date };

    const filename =
      this.visitsSummaryMode === 'single'
        ? `employee_visits_${date}.xlsx`
        : `employee_visits_${from_date}_to_${to_date}.xlsx`;

    this.isExportingVisitsSummary = true;

    this._subs.add(
      this._employeesService
        .exportVisitsSummary(filters)
        .pipe(finalize(() => (this.isExportingVisitsSummary = false)))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          },
        })
    );
  }

  private _getEmployeesList(): void {
    this.isEmployeesDataLoading = true;

    const reqParams = buildFilterQuery({
      page: this.currentPage,
      search: this.searchTerm,
      team: 'sales',
      designation: this.selectedDesignation,
    });

    this._employeesSubscription = this._employeesService
      .list(reqParams)
      .pipe(
        finalize(() => {
          this.isEmployeesDataLoading = false;
        })
      )
      .subscribe({
        next: (employeesList: PaginatedResponse<Employee>) => {
          this.employees = employeesList.results || [];
          this.hasNextPage = !!employeesList.next;
          this.hasPreviousPage = !!employeesList.previous;
          this.totalCount = employeesList.count;
        },
      });
  }

  private _loadDesignations(): void {
    this._subs.add(
      this._employeesService.getDesignations().subscribe({
        next: designations => (this.designations = designations),
      })
    );
  }

  private _loadDealers(): void {
    this._subs.add(
      this._dealersService.lighter_list().subscribe({
        next: (dealers: Dealer[]) => (this.dealersList = dealers),
      })
    );
  }

  ngOnDestroy(): void {
    this._employeesSubscription?.unsubscribe();
    this._subs.unsubscribe();
  }

  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;
  protected readonly handleImageError = handleImageError;
}
